import { Dispatch, SetStateAction, useRef } from 'react';
import ReactSlider from 'react-slider';
import SharedButton from '../../../../global/Button/Button';
import { ScoreHistoryPanelState } from '../utils/searchResultsHelpers';

export type ScoreHistorySelectionCommit = {
  year?: number | null;
  yearFrom?: number | null;
  yearTo?: number | null;
};

interface ScoreHistoryPanelProps {
  panel: ScoreHistoryPanelState;
  onUpdate: Dispatch<SetStateAction<ScoreHistoryPanelState>>;
  onClose?: () => void;
  hideHeader?: boolean;
  onSelectionCommit: (detail: ScoreHistorySelectionCommit) => void;
  minAllowedYear?: number;
  maxAllowedYear?: number;
}

export function ScoreHistoryPanel({
  panel,
  onUpdate,
  onClose,
  hideHeader,
  onSelectionCommit,
  minAllowedYear: minAllowedYearProp,
  maxAllowedYear: maxAllowedYearProp,
}: ScoreHistoryPanelProps) {
  const calendarYear = new Date().getFullYear();
  const minAllowedYear = minAllowedYearProp ?? 1900;
  const maxAllowedYear = maxAllowedYearProp ?? calendarYear;

  const suggestedYears = [maxAllowedYear, maxAllowedYear - 1, maxAllowedYear - 2, maxAllowedYear - 5].filter(
    (year, index, array) => year >= minAllowedYear && year <= maxAllowedYear && array.indexOf(year) === index,
  );

  const { yearInput, yearFromInput, yearToInput } = panel;

  const sliderReleaseRef = useRef<[number, number]>([
    panel.selectedYearFrom ?? minAllowedYear,
    panel.selectedYearTo ?? maxAllowedYear,
  ]);

  const applyYear = (year: number | null) => {
    onUpdate(prev => ({
      ...prev,
      selectedYear: year,
      selectedYearFrom: null,
      selectedYearTo: null,
      yearInput: year != null ? String(year) : '',
      yearFromInput: '',
      yearToInput: '',
    }));
    onSelectionCommit({
      year,
      yearFrom: null,
      yearTo: null,
    });
  };

  const applyYearRange = (yearFrom: number | null, yearTo: number | null) => {
    onUpdate(prev => ({
      ...prev,
      selectedYear: null,
      yearInput: '',
      selectedYearFrom: yearFrom,
      selectedYearTo: yearTo,
      yearFromInput: yearFrom != null ? String(yearFrom) : '',
      yearToInput: yearTo != null ? String(yearTo) : '',
    }));
    onSelectionCommit({
      year: null,
      yearFrom,
      yearTo,
    });
  };

  const tryApplyInputYear = () => {
    const raw = yearInput.trim();
    if (raw === '') {
      applyYear(null);
      return;
    }
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return;
    const normalized = Math.trunc(parsed);
    if (normalized < minAllowedYear || normalized > maxAllowedYear) return;
    applyYear(normalized);
  };

  const tryApplyInputYearRange = () => {
    const rawFrom = yearFromInput.trim();
    const rawTo = yearToInput.trim();
    if (rawFrom === '' || rawTo === '') return;
    const parsedFrom = Number(rawFrom);
    const parsedTo = Number(rawTo);
    if (!Number.isFinite(parsedFrom) || !Number.isFinite(parsedTo)) return;
    const normalizedFrom = Math.trunc(parsedFrom);
    const normalizedTo = Math.trunc(parsedTo);
    if (normalizedFrom < minAllowedYear || normalizedTo < minAllowedYear) return;
    if (normalizedFrom > maxAllowedYear || normalizedTo > maxAllowedYear) return;
    if (normalizedFrom > normalizedTo) return;
    applyYearRange(normalizedFrom, normalizedTo);
  };

  const rangeFrom = panel.selectedYearFrom ?? minAllowedYear;
  const rangeTo = panel.selectedYearTo ?? maxAllowedYear;

  return (
    <>
      {!hideHeader && (
        <div className="flex items-center justify-between flex-shrink-0 px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="text-sm font-semibold text-gray-900 truncate min-w-0 mb-0">Score history</h2>
          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-200 flex-shrink-0"
              aria-label="Close"
            >
              ×
            </button>
          ) : null}
        </div>
      )}
      <div className={`flex-1 overflow-y-auto min-h-0 ${hideHeader ? 'px-1 pt-0 pb-1' : 'p-3'}`}>
        <p className="text-xs text-gray-600 mb-2">
          Select a year to emulate score calculation with evidence from that year.
        </p>
        <label htmlFor="score-history-year-input" className="text-xs font-medium text-gray-700 mb-1 block">
          Score year
        </label>
        <input
          id="score-history-year-input"
          type="number"
          min={minAllowedYear}
          max={maxAllowedYear}
          placeholder={`e.g. ${maxAllowedYear - 1}`}
          className="filter-input w-full !mt-0"
          value={yearInput}
          onChange={e => onUpdate(prev => ({ ...prev, yearInput: e.target.value }))}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              tryApplyInputYear();
            }
          }}
        />
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {suggestedYears.map(year => (
            <button
              key={year}
              type="button"
              className="rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-xs font-medium text-violet-800 hover:bg-violet-100"
              onClick={() => applyYear(year)}
            >
              {year}
            </button>
          ))}
        </div>
        <div className="mt-4 border-t border-gray-200 pt-3">
          <p className="mb-2 text-xs font-medium text-gray-700">Or choose a range</p>
          <div className="grid grid-cols-2 gap-2">
            <input
              type="number"
              min={minAllowedYear}
              max={maxAllowedYear}
              placeholder="From"
              className="filter-input w-full !mt-0"
              value={yearFromInput}
              onChange={e => onUpdate(prev => ({ ...prev, yearFromInput: e.target.value }))}
            />
            <input
              type="number"
              min={minAllowedYear}
              max={maxAllowedYear}
              placeholder="To"
              className="filter-input w-full !mt-0"
              value={yearToInput}
              onChange={e => onUpdate(prev => ({ ...prev, yearToInput: e.target.value }))}
            />
          </div>
          <div className="mt-3">
            <ReactSlider
              className="relative flex h-11 items-center px-1 sm:h-9"
              thumbClassName="h-7 w-7 rounded-full border-2 border-blue bg-white shadow-md cursor-grab active:cursor-grabbing focus:outline-none focus:ring-2 focus:ring-blue-300 sm:h-5 sm:w-5"
              trackClassName="h-2 rounded-full"
              min={minAllowedYear}
              max={maxAllowedYear}
              pearling
              minDistance={0}
              value={[rangeFrom, rangeTo]}
              onChange={values => {
                const [nextFrom, nextTo] = values.map(v => Math.trunc(Number(v)));
                if (nextFrom > nextTo) return;
                sliderReleaseRef.current = [nextFrom, nextTo];
                onUpdate(prev => ({
                  ...prev,
                  selectedYear: null,
                  yearInput: '',
                  selectedYearFrom: nextFrom,
                  selectedYearTo: nextTo,
                  yearFromInput: String(nextFrom),
                  yearToInput: String(nextTo),
                }));
              }}
              onAfterChange={() => {
                const [from, to] = sliderReleaseRef.current;
                if (from > to) return;
                onSelectionCommit({
                  year: null,
                  yearFrom: from,
                  yearTo: to,
                });
              }}
              renderTrack={(props, state) => {
                const { key, ...trackProps } = props;
                return (
                  <div
                    key={key}
                    {...trackProps}
                    className={`${props.className} ${
                      state.index === 1 ? 'bg-blue shadow-sm' : 'bg-slate-300'
                    }`}
                  />
                );
              }}
            />
            <div className="mt-1 flex items-center justify-between text-[11px] text-gray-600">
              <span>{rangeFrom}</span>
              <span>{rangeTo}</span>
            </div>
          </div>
        </div>
        <div className="row">
          <SharedButton
            variant="secondary"
            size="xs"
            onClick={tryApplyInputYearRange}
            className="ml-0 mt-3 mb-0"
          >
            Apply range
          </SharedButton>
          <SharedButton variant="secondary" size="xs" onClick={tryApplyInputYear} className="ml-1 mt-3 mb-0">
            Search
          </SharedButton>
        </div>
      </div>
    </>
  );
}
